import { useState, useCallback, useRef } from 'react';

/**
 * Hook to manage visual editor state and operations
 * Handles selection, property extraction, and data updates
 */
export function useVisualEditor(initialData, onDataChange) {
  const [selectedElement, setSelectedElement] = useState(null);
  const [isVisualEditMode, setIsVisualEditMode] = useState(false);
  const [hoveredElement, setHoveredElement] = useState(null);
  const dataRef = useRef(initialData);

  // Update data ref when initialData changes
  if (dataRef.current !== initialData) {
    dataRef.current = initialData;
  }

  /**
   * Handle element selection
   */
  const handleElementClick = useCallback((event, elementInfo) => {
    if (!isVisualEditMode) return;
    
    event.stopPropagation();
    event.preventDefault();
    
    setSelectedElement(elementInfo);
  }, [isVisualEditMode]);

  /**
   * Handle element hover
   */
  const handleElementHover = useCallback((event, elementInfo) => {
    if (!isVisualEditMode || selectedElement) return;
    
    setHoveredElement(elementInfo);
  }, [isVisualEditMode, selectedElement]);

  /**
   * Clear hover state
   */
  const handleElementLeave = useCallback(() => {
    setHoveredElement(null);
  }, []);

  /**
   * Update property in data structure
   * Handles nested collateral.collateral.data structure
   */
  const updateProperty = useCallback((path, value) => {
    if (!path || !onDataChange) return;

    console.log('🔧 Updating property:', path, 'to:', value);

    // Skip internal properties (starting with _)
    if (path.includes('._')) {
      console.log('Skipping internal property:', path);
      return;
    }

    const newData = JSON.parse(JSON.stringify(dataRef.current));
    
    // Handle nested collateral structure
    // If data has collateral.collateral.data, navigate there first
    let root = newData;
    if (newData.collateral?.collateral?.data) {
      console.log('📦 Detected nested collateral structure, navigating to collateral.collateral.data');
      root = newData.collateral.collateral.data;
    } else if (newData.collateral?.data) {
      console.log('📦 Detected collateral.data structure');
      root = newData.collateral.data;
    }
    
    // Navigate to the property and update it
    const pathParts = path.split('.');
    let current = root;
    
    try {
      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        const arrayMatch = part.match(/(.+)\[(\d+)\]/);
        
        if (arrayMatch) {
          const [, key, index] = arrayMatch;
          if (!current[key]) current[key] = [];
          if (!current[key][parseInt(index)]) current[key][parseInt(index)] = {};
          current = current[key][parseInt(index)];
        } else {
          if (!current[part]) current[part] = {};
          current = current[part];
        }
      }
      
      const lastPart = pathParts[pathParts.length - 1];
      const arrayMatch = lastPart.match(/(.+)\[(\d+)\]/);
      
      if (arrayMatch) {
        const [, key, index] = arrayMatch;
        if (!current[key]) current[key] = [];
        current[key][parseInt(index)] = value;
      } else {
        current[lastPart] = value;
      }
      
      console.log('✅ Updated data successfully');
      console.log('New value at path:', current[lastPart] || current[arrayMatch?.[1]]?.[parseInt(arrayMatch?.[2])]);
      
      dataRef.current = newData;
      onDataChange(newData);
    } catch (error) {
      console.error('❌ Error updating property:', error);
      console.log('Path parts:', pathParts);
      console.log('Current object:', current);
      throw error;
    }
  }, [onDataChange]);

  /**
   * Toggle visual edit mode
   */
  const toggleVisualEditMode = useCallback(() => {
    setIsVisualEditMode(prev => !prev);
    if (isVisualEditMode) {
      setSelectedElement(null);
      setHoveredElement(null);
    }
  }, [isVisualEditMode]);

  /**
   * Clear selection
   */
  const clearSelection = useCallback(() => {
    setSelectedElement(null);
  }, []);

  return {
    // State
    selectedElement,
    isVisualEditMode,
    hoveredElement,
    
    // Actions
    handleElementClick,
    handleElementHover,
    handleElementLeave,
    updateProperty,
    toggleVisualEditMode,
    clearSelection,
    setSelectedElement,
    setIsVisualEditMode,
  };
}

